/**
 * FileStorageService.ts — WorkRoamer Object Storage™ client-side wrapper.
 *
 * Single point of contact for all file upload/download/delete operations in
 * the browser. Wraps the /api/files/* REST API (Sprint 3 — FileStorageManager™).
 *
 * Rules:
 *   - Uses authFetch (not raw fetch) so Bearer token is always injected before
 *     the fetch interceptor chain — same pattern as Pipeline 1 (customFetch).
 *   - All errors are logged before re-throwing so callers get structured context
 *   - Never store file bytes in localStorage or sessionStorage — R2 only
 *   - Uploads automatically deduplicate server-side (SHA-256)
 */

import { authFetch } from '@/lib/authFetch';

// ─── Types (mirrors server FileRecord) ────────────────────────────────────────

export interface FileRecord {
  id:                 number;
  userId:             number;
  storageKey:         string;
  sha256:             string;
  mimeType:           string;
  sizeBytes:          number;
  storedBytes:        number;
  originalName:       string | null;
  tier:               string;
  thumbnailKey:       string | null;
  thumbnailStatus:    string;
  compressed:         boolean;
  compressionSavings: number;
  deduped:            boolean;
  refCount:           number;
  createdAt:          string;
  lastAccessedAt:     string;
}

export interface SignedUrlResult {
  url:       string;
  expiresAt: string;
}

export interface StorageStats {
  totalFiles:         number;
  totalSizeBytes:     number;
  totalStoredBytes:   number;
  dedupedFiles:       number;
  compressedFiles:    number;
  compressionSavings: number;
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

const BASE = '/api/files';

function log(event: string, detail: Record<string, unknown>): void {
  console.log(`[FileStorageService] ${event}`, detail);
}

function logError(event: string, err: unknown, detail?: Record<string, unknown>): void {
  console.error(`[FileStorageService] ${event}`, { error: err, ...detail });
}

async function api<T>(path: string, opts: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, opts);
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(body.error ?? `Storage API error ${res.status} at ${path}`);
  }
  return res.json() as Promise<T>;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Upload a File or Blob to Cloudflare R2 via the server pipeline.
 * Handles deduplication, compression, and thumbnail queuing automatically.
 *
 * @param file      Browser File or Blob to upload.
 * @param name      Original filename (used for dedup key and content-type inference).
 * @param mimeType  Override MIME type (defaults to file.type).
 * @returns The persisted FileRecord with an id for future signed URL requests.
 */
export async function uploadFile(
  file:     File | Blob,
  name:     string,
  mimeType?: string,
): Promise<FileRecord> {
  const form = new FormData();
  const blob = mimeType ? new Blob([file], { type: mimeType }) : file;
  form.append('file', blob, name);

  const startMs = Date.now();
  try {
    const result = await api<{ file: FileRecord; deduped: boolean; message: string }>(
      '/upload',
      { method: 'POST', body: form },
    );
    log('upload:ok', {
      fileId: result.file.id,
      name,
      sizeBytes: result.file.sizeBytes,
      deduped: result.deduped,
      compressed: result.file.compressed,
      durationMs: Date.now() - startMs,
    });
    return result.file;
  } catch (err) {
    logError('upload:failed', err, { name, sizeBytes: file.size });
    throw err;
  }
}

/**
 * Convenience: upload a file and immediately request a signed read URL.
 * Returns both the record (for future use) and a URL ready for display.
 */
export async function uploadAndGetUrl(
  file:     File | Blob,
  name:     string,
  mimeType?: string,
  ttl       = 3600,
): Promise<{ record: FileRecord; url: string; expiresAt: string }> {
  const record = await uploadFile(file, name, mimeType);
  const signed = await getSignedUrl(record.id, ttl);
  return { record, ...signed };
}

/**
 * Generate a signed read URL for a file already stored in R2.
 * @param fileId  The FileRecord.id returned by uploadFile.
 * @param ttl     URL lifetime in seconds (default 1 h, max 24 h).
 */
export async function getSignedUrl(fileId: number, ttl = 3600): Promise<SignedUrlResult> {
  try {
    const result = await api<SignedUrlResult>(
      `/${fileId}/url${ttl !== 3600 ? `?ttl=${ttl}` : ''}`,
      { method: 'GET' },
    );
    log('signedUrl:ok', { fileId, expiresAt: result.expiresAt });
    return result;
  } catch (err) {
    logError('signedUrl:failed', err, { fileId });
    throw err;
  }
}

/**
 * Generate a signed URL for a file thumbnail (images only).
 * Returns null when no thumbnail is ready (unsupported type or still processing).
 */
export async function getThumbnailUrl(fileId: number, ttl = 3600): Promise<SignedUrlResult | null> {
  try {
    const result = await api<SignedUrlResult>(
      `/${fileId}/thumb-url${ttl !== 3600 ? `?ttl=${ttl}` : ''}`,
      { method: 'GET' },
    );
    return result;
  } catch (err: unknown) {
    // 404 = thumbnail not ready — not an error
    if ((err as { message?: string }).message?.includes('404') ||
        (err as { message?: string }).message?.includes('not available')) {
      return null;
    }
    logError('thumbnailUrl:failed', err, { fileId });
    return null;
  }
}

/**
 * Delete a file from R2. Decrements ref count; hard-deletes when count reaches 0.
 * Ownership is enforced server-side.
 */
export async function deleteFile(fileId: number): Promise<void> {
  try {
    await api<{ ok: boolean }>(`/${fileId}`, { method: 'DELETE' });
    log('delete:ok', { fileId });
  } catch (err) {
    logError('delete:failed', err, { fileId });
    throw err;
  }
}

/**
 * List files owned by the authenticated user.
 */
export async function listFiles(limit = 50, offset = 0): Promise<FileRecord[]> {
  try {
    const res = await api<{ files: FileRecord[] }>(
      `?limit=${limit}&offset=${offset}`,
      { method: 'GET' },
    );
    return res.files;
  } catch (err) {
    logError('list:failed', err);
    throw err;
  }
}

/**
 * Fetch storage statistics for the authenticated user.
 */
export async function getStorageStats(): Promise<StorageStats> {
  try {
    const res = await api<{ user: StorageStats }>('/stats', { method: 'GET' });
    return res.user;
  } catch (err) {
    logError('stats:failed', err);
    throw err;
  }
}

/**
 * Convert a base64 data URL to a Blob and upload it to R2.
 * Used for migrating existing base64 avatars and backpack items.
 *
 * @param dataUrl  A "data:<mime>;base64,..." string.
 * @param name     Filename to assign.
 * @returns FileRecord on success, null if the dataUrl is invalid.
 */
export async function uploadDataUrl(dataUrl: string, name: string): Promise<FileRecord | null> {
  try {
    const [header, b64] = dataUrl.split(',');
    if (!header || !b64) return null;
    const mimeType = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream';
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: mimeType });
    return await uploadFile(blob, name, mimeType);
  } catch (err) {
    logError('uploadDataUrl:failed', err, { name });
    return null;
  }
}
